import type {
  Ministry,
  Person,
  Position,
  ServiceType,
  Team,
  TeamMembership,
} from '@/types'
import { nowISO } from '@/lib/utils'

const ts = () => nowISO()

export function buildMinistries(): Ministry[] {
  const now = ts()
  return [
    { id: 'min_worship', name: 'Worship', description: 'Music and creative arts', color: '#6366f1', sortOrder: 1, createdAt: now, updatedAt: now },
    { id: 'min_production', name: 'Production', description: 'Audio, video and lighting', color: '#0ea5e9', sortOrder: 2, createdAt: now, updatedAt: now },
    { id: 'min_hospitality', name: 'Hospitality', description: 'Welcome, ushers and cafe', color: '#f59e0b', sortOrder: 3, createdAt: now, updatedAt: now },
    { id: 'min_kids', name: 'Kids & Youth', description: 'MDV Kids and youth ministry', color: '#22c55e', sortOrder: 4, createdAt: now, updatedAt: now },
  ]
}

export function buildTeams(): Team[] {
  const now = ts()
  const t = (id: string, ministryId: string, name: string, description: string, sortOrder: number): Team => ({
    id, ministryId, name, description, sortOrder, createdAt: now, updatedAt: now,
  })
  return [
    t('team_band', 'min_worship', 'Band', 'Instrumentalists', 1),
    t('team_vocals', 'min_worship', 'Vocals', 'Worship leaders and backing vocals', 2),
    t('team_audio', 'min_production', 'Audio', 'Front of house and monitors', 3),
    t('team_visuals', 'min_production', 'Visuals', 'Slides, cameras and stream', 4),
    t('team_hosts', 'min_hospitality', 'Welcome Team', 'Door hosts and ushers', 5),
    t('team_kids', 'min_kids', 'Kids Church', 'Sunday morning kids programme', 6),
  ]
}

export function buildPositions(): Position[] {
  const now = ts()
  const p = (id: string, teamId: string, name: string, defaultQuantity: number, sortOrder: number): Position => ({
    id, teamId, name, defaultQuantity, sortOrder, createdAt: now, updatedAt: now,
  })
  return [
    p('pos_bass', 'team_band', 'Bass Guitar', 1, 1),
    p('pos_electric', 'team_band', 'Electric Guitar', 1, 2),
    p('pos_acoustic', 'team_band', 'Acoustic Guitar', 1, 3),
    p('pos_keys', 'team_band', 'Keys', 1, 4),
    p('pos_drums', 'team_band', 'Drums', 1, 5),
    p('pos_leader', 'team_vocals', 'Worship Leader', 1, 6),
    p('pos_bv', 'team_vocals', 'Backing Vocal', 2, 7),
    p('pos_foh', 'team_audio', 'FOH Engineer', 1, 8),
    p('pos_monitors', 'team_audio', 'Monitor Engineer', 1, 9),
    p('pos_propresenter', 'team_visuals', 'ProPresenter Operator', 1, 10),
    p('pos_camera', 'team_visuals', 'Camera Operator', 2, 11),
    p('pos_stream', 'team_visuals', 'Stream Director', 1, 12),
    p('pos_welcome', 'team_hosts', 'Welcome Host', 2, 13),
    p('pos_usher', 'team_hosts', 'Usher', 2, 14),
    p('pos_kids_lead', 'team_kids', 'Kids Lead', 1, 15),
    p('pos_kids_helper', 'team_kids', 'Kids Helper', 2, 16),
  ]
}

interface PersonSeed {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  role: Person['role']
  color: string
  teamIds: string[]
  notes?: string
}

const PEOPLE: PersonSeed[] = [
  { id: 'person_ian', firstName: 'Ian', lastName: 'Vaughn', email: 'ian@mymdv.org', phone: '+1 555 0100', role: 'admin', color: '#6366f1', teamIds: ['team_vocals', 'team_band'], notes: 'Services administrator.' },
  { id: 'person_maria', firstName: 'Maria', lastName: 'Delgado', email: 'maria@mymdv.org', phone: '+1 555 0111', role: 'scheduler', color: '#ec4899', teamIds: ['team_vocals'], notes: 'Schedules the worship rota.' },
  { id: 'person_tom', firstName: 'Tom', lastName: 'Whitfield', email: 'tom@mymdv.org', role: 'member', color: '#0ea5e9', teamIds: ['team_band'] },
  { id: 'person_grace', firstName: 'Grace', lastName: 'Okonkwo', email: 'grace@mymdv.org', role: 'member', color: '#f59e0b', teamIds: ['team_vocals'] },
  { id: 'person_daniel', firstName: 'Daniel', lastName: 'Reyes', email: 'daniel@mymdv.org', role: 'member', color: '#22c55e', teamIds: ['team_band'] },
  { id: 'person_hannah', firstName: 'Hannah', lastName: 'Bergstrom', email: 'hannah@mymdv.org', role: 'member', color: '#a855f7', teamIds: ['team_band', 'team_vocals'] },
  { id: 'person_kevin', firstName: 'Kevin', lastName: 'Osei', email: 'kevin@mymdv.org', role: 'member', color: '#ef4444', teamIds: ['team_audio'] },
  { id: 'person_priya', firstName: 'Priya', lastName: 'Nair', email: 'priya@mymdv.org', role: 'scheduler', color: '#14b8a6', teamIds: ['team_audio', 'team_visuals'], notes: 'Production lead.' },
  { id: 'person_luke', firstName: 'Luke', lastName: 'Bennett', email: 'luke@mymdv.org', role: 'member', color: '#8b5cf6', teamIds: ['team_visuals'] },
  { id: 'person_sofia', firstName: 'Sofia', lastName: 'Marchetti', email: 'sofia@mymdv.org', role: 'member', color: '#f97316', teamIds: ['team_visuals'] },
  { id: 'person_james', firstName: 'James', lastName: 'Turner', email: 'james@mymdv.org', role: 'member', color: '#3b82f6', teamIds: ['team_hosts'] },
  { id: 'person_ruth', firstName: 'Ruth', lastName: 'Adeyemi', email: 'ruth@mymdv.org', role: 'member', color: '#d946ef', teamIds: ['team_hosts', 'team_kids'] },
  { id: 'person_peter', firstName: 'Peter', lastName: 'Novak', email: 'peter@mymdv.org', role: 'member', color: '#64748b', teamIds: ['team_hosts'] },
  { id: 'person_ana', firstName: 'Ana', lastName: 'Silva', email: 'ana@mymdv.org', role: 'member', color: '#10b981', teamIds: ['team_kids'] },
  { id: 'person_chris', firstName: 'Chris', lastName: 'Larkin', email: 'chris@mymdv.org', role: 'member', color: '#eab308', teamIds: ['team_kids'] },
  { id: 'person_naomi', firstName: 'Naomi', lastName: 'Fletcher', email: 'naomi@mymdv.org', role: 'member', color: '#06b6d4', teamIds: ['team_band', 'team_audio'] },
]

export const CURRENT_PERSON_ID = 'person_ian'

export function buildPeople(): Person[] {
  const now = ts()
  return PEOPLE.map((p) => ({
    id: p.id,
    firstName: p.firstName,
    lastName: p.lastName,
    email: p.email,
    phone: p.phone,
    role: p.role,
    avatarColor: p.color,
    teamIds: p.teamIds,
    notes: p.notes,
    createdAt: now,
    updatedAt: now,
  }))
}

interface MembershipSeed {
  personId: string
  teamId: string
  positionIds: string[]
  proficiency: TeamMembership['proficiency']
}

const MEMBERSHIPS: MembershipSeed[] = [
  { personId: 'person_ian', teamId: 'team_vocals', positionIds: ['pos_leader', 'pos_bv'], proficiency: 'lead' },
  { personId: 'person_ian', teamId: 'team_band', positionIds: ['pos_acoustic'], proficiency: 'qualified' },
  { personId: 'person_maria', teamId: 'team_vocals', positionIds: ['pos_leader', 'pos_bv'], proficiency: 'lead' },
  { personId: 'person_grace', teamId: 'team_vocals', positionIds: ['pos_bv'], proficiency: 'qualified' },
  { personId: 'person_hannah', teamId: 'team_vocals', positionIds: ['pos_bv'], proficiency: 'qualified' },
  { personId: 'person_hannah', teamId: 'team_band', positionIds: ['pos_keys'], proficiency: 'lead' },
  { personId: 'person_tom', teamId: 'team_band', positionIds: ['pos_bass'], proficiency: 'lead' },
  { personId: 'person_daniel', teamId: 'team_band', positionIds: ['pos_drums'], proficiency: 'qualified' },
  { personId: 'person_naomi', teamId: 'team_band', positionIds: ['pos_electric', 'pos_acoustic'], proficiency: 'qualified' },
  { personId: 'person_naomi', teamId: 'team_audio', positionIds: ['pos_monitors'], proficiency: 'training' },
  { personId: 'person_kevin', teamId: 'team_audio', positionIds: ['pos_foh', 'pos_monitors'], proficiency: 'lead' },
  { personId: 'person_priya', teamId: 'team_audio', positionIds: ['pos_foh'], proficiency: 'qualified' },
  { personId: 'person_priya', teamId: 'team_visuals', positionIds: ['pos_stream', 'pos_propresenter'], proficiency: 'lead' },
  { personId: 'person_luke', teamId: 'team_visuals', positionIds: ['pos_camera', 'pos_stream'], proficiency: 'qualified' },
  { personId: 'person_sofia', teamId: 'team_visuals', positionIds: ['pos_propresenter', 'pos_camera'], proficiency: 'qualified' },
  { personId: 'person_james', teamId: 'team_hosts', positionIds: ['pos_welcome', 'pos_usher'], proficiency: 'lead' },
  { personId: 'person_ruth', teamId: 'team_hosts', positionIds: ['pos_welcome'], proficiency: 'qualified' },
  { personId: 'person_ruth', teamId: 'team_kids', positionIds: ['pos_kids_lead'], proficiency: 'lead' },
  { personId: 'person_peter', teamId: 'team_hosts', positionIds: ['pos_usher'], proficiency: 'training' },
  { personId: 'person_ana', teamId: 'team_kids', positionIds: ['pos_kids_helper'], proficiency: 'qualified' },
  { personId: 'person_chris', teamId: 'team_kids', positionIds: ['pos_kids_helper', 'pos_kids_lead'], proficiency: 'qualified' },
]

export function buildMemberships(): TeamMembership[] {
  const now = ts()
  return MEMBERSHIPS.map((m, index) => ({
    id: `mem_${String(index + 1).padStart(2, '0')}`,
    personId: m.personId,
    teamId: m.teamId,
    positionIds: m.positionIds,
    proficiency: m.proficiency,
    createdAt: now,
    updatedAt: now,
  }))
}

export function buildServiceTypes(): ServiceType[] {
  const now = ts()
  return [
    {
      id: 'stype_sunday_930',
      name: 'Sunday 9:30 Gathering',
      ministryId: 'min_worship',
      teamIds: ['team_vocals', 'team_band', 'team_audio', 'team_visuals', 'team_hosts', 'team_kids'],
      defaultStartTime: '09:30',
      sortOrder: 1,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'stype_sunday_1130',
      name: 'Sunday 11:30 Gathering',
      ministryId: 'min_worship',
      teamIds: ['team_vocals', 'team_band', 'team_audio', 'team_visuals', 'team_hosts'],
      defaultStartTime: '11:30',
      sortOrder: 2,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'stype_midweek',
      name: 'Midweek Prayer',
      ministryId: 'min_worship',
      teamIds: ['team_vocals', 'team_audio'],
      defaultStartTime: '19:00',
      sortOrder: 3,
      createdAt: now,
      updatedAt: now,
    },
  ]
}
